import * as React from "react";
import { Area, AreaChart, CartesianGrid, XAxis, LabelList, Line } from "recharts";
import { ChartConfig, ChartContainer, ChartTooltip } from "@/components/shadcn/chart";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/shadcn/tooltip";
import { Checkbox } from "@/components/shadcn/checkbox";
import { type PlayerData } from "@/api/player";
import dayjs from "dayjs";
import { dateFormat } from "@/lib/utils";
import { Loader } from "../../common/loader";
import { calculateTrendLine, TrendLabel } from "./chartUtils";
import { useIsMobile } from "@/hooks/use-mobile";

interface DetailedChartDataPoint {
    date: string;
    matchDate: string;
    matchNumber: number;
    accurateShots?: number;
    missedShots?: number;
    totalShots?: number;
    goalRate?: number;
    accurateShotRate?: number;
    successfulPassesData?: number;
    successfulPassesUnderPressure?: number;
    successfulPassesUnderHighPressure?: number;
    failedPassesData?: number;
    failedPassesUnderPressure?: number;
    failedPassesUnderHighPressure?: number;
    successPassRate?: number;
    successPassUnderPressureRate?: number;
    successPassUnderHighPressureRate?: number;
    totalXg?: number;
    xgFor?: number;
    pressureReceived?: number;
    averagePressureReceived?: number;
    fullStrengthShootingPercentage?: number;
    puckProtectionPPMTotal?: number;
    puckProtectionPPMOffensive?: number;
    puckProtectionPPMDefensive?: number;
    puckProtectionPPMNeutral?: number;
    puckProtectionEventsTotal?: number;
    puckProtectionEventsOffensive?: number;
    puckProtectionEventsDefensive?: number;
    puckProtectionEventsNeutral?: number;
    turnoversTotal?: number;
    turnoversOffensive?: number;
    turnoversDefensive?: number;
    turnoversNeutral?: number;
    averagePressureGiven?: number;
    pressureGiven?: number;
    highRiskShotsAgainst?: number;
    xgAgainst?: number;
    fullStrengthSavePercentage?: number;
    blockedShots?: number;
    puckClearances?: number;
    puckContestsWinRate?: number;
    contestsTotal?: number;
    contestsOffensive?: number;
    contestsDefensive?: number;
    contestsNeutral?: number;
    contestsWonTotal?: number;
    contestsWonOffensive?: number;
    contestsWonDefensive?: number;
    contestsWonNeutral?: number;
    takeawaysOffensive?: number;
    takeawaysDefensive?: number;
    takeawaysNeutral?: number;
    plusMinusRaw?: number;
    corsiForPercentage?: number;
    relativeCorsiForPercentage?: number;
    fenwickForPercentage?: number;
    relativeFenwickForPercentage?: number;
    penaltyMinutes?: number;
    takeawaysTotal?: number;
    topSpeed?: number;
    distancePer20?: number;
    distanceTravelled?: number;
    accelerationsPer20?: number;
    decelerationsPer20?: number;
    averageSpeedWithPuck?: number;
    puckControlForwardDistance?: number;
    puckControlForwardDistancePercentage?: number;
    goals?: number;
    assists?: number;
    timeOnIceTotal?: number;
    timeZoneOffensiveMinutes?: number;
    timeZoneDefensiveMinutes?: number;
    timeZoneNeutralMinutes?: number;
    timeZoneOffensivePercentage?: number;
    timeZoneDefensivePercentage?: number;
    timeZoneNeutralPercentage?: number;
    averageShiftLength?: number;
    offensivePoints?: number;
    defensivePoints?: number;
    teamPlayPoints?: number;
    effortPoints?: number;
    bonusPoints?: number;
    [key: string]: string | number | undefined;
}

const kpiRawDataMapping = {
    totalPoints: {
        title: "Total Points Breakdown",
        secondaryTitle: null,
        metrics: [
            { key: "offensivePoints", label: "Offensive Points", shortLabel: "OFF", color: "var(--color-general-red)" },
            {
                key: "defensivePoints",
                label: "Defensive Points",
                shortLabel: "DEF",
                color: "var(--color-general-green)",
            },
            {
                key: "teamPlayPoints",
                label: "Team Play Points",
                shortLabel: "TP",
                color: "var(--color-general-yellow)",
            },
            { key: "effortPoints", label: "Effort Points", shortLabel: "EFF", color: "var(--color-general-purple)" },
            { key: "bonusPoints", label: "Bonus Points", shortLabel: "BP", color: "var(--color-general-orange)" },
        ],
    },
    offensivePoints: {
        title: "Offensive Performance",
        secondaryTitle: null,
        metrics: [
            { key: "goalRate", label: "Goal Rate (%)", shortLabel: "GR", color: "#ef4444" },
            { key: "accurateShotRate", label: "Accurate Shot Rate (%)", shortLabel: "ASR", color: "#ff8c00" },
            { key: "totalXg", label: "Expected Goals (xG)", shortLabel: "xG", color: "#ff1493" },
            { key: "successPassRate", label: "Pass Success Rate (%)", shortLabel: "PSR", color: "#dc143c" },
            {
                key: "successPassUnderPressureRate",
                label: "Pass Success Under Pressure (%)",
                shortLabel: "PSUP",
                color: "#b91c1c",
            },
            {
                key: "successPassUnderHighPressureRate",
                label: "Pass Success Under High Pressure (%)",
                shortLabel: "PSHP",
                color: "#991b1b",
            },
            { key: "averagePressureReceived", label: "Avg Pressure Received (%)", shortLabel: "APR", color: "#f87171" },
            {
                key: "fullStrengthShootingPercentage",
                label: "Shooting Percentage (%)",
                shortLabel: "SH%",
                color: "#dc2626",
            },
            { key: "turnoversTotal", label: "Turnovers by Zone", shortLabel: "TO", color: "#ff6b6b" },
            { key: "puckProtectionEventsTotal", label: "Puck Protection Events", shortLabel: "PPE", color: "#ff9999" },
        ],
    },
    defensivePoints: {
        title: "Defensive Performance",
        secondaryTitle: null,
        metrics: [
            { key: "averagePressureGiven", label: "Avg Pressure Given (%)", shortLabel: "APG", color: "#00ff00" },
            { key: "fullStrengthSavePercentage", label: "Save Percentage (%)", shortLabel: "SV%", color: "#00ced1" },
            { key: "blockedShots", label: "Blocked Shots", shortLabel: "BLK", color: "#32cd32" },
            { key: "puckClearances", label: "Puck Clearances", shortLabel: "PC", color: "#20b2aa" },
            { key: "puckContestsWinRate", label: "Puck Contest Win Rate (%)", shortLabel: "PCWR", color: "#40e0d0" },
            { key: "contestsTotal", label: "Total Contests", shortLabel: "TC", color: "#48d1cc" },
            { key: "contestsWonTotal", label: "Contests Won", shortLabel: "CW", color: "#66cdaa" },
        ],
    },
    teamPlayPoints: {
        title: "Team Play Statistics",
        secondaryTitle: null,
        metrics: [
            { key: "plusMinusRaw", label: "Plus/Minus", shortLabel: "+/-", color: "#ffd700" },
            { key: "corsiForPercentage", label: "Corsi For (%)", shortLabel: "CF%", color: "#ff8c00" },
            {
                key: "relativeCorsiForPercentage",
                label: "Relative Corsi For (%)",
                shortLabel: "RCF%",
                color: "#ff9500",
            },
            { key: "fenwickForPercentage", label: "Fenwick For (%)", shortLabel: "FF%", color: "#ffb347" },
            {
                key: "relativeFenwickForPercentage",
                label: "Relative Fenwick For (%)",
                shortLabel: "RFF%",
                color: "#ffbf69",
            },
            { key: "penaltyMinutes", label: "Penalty Minutes", shortLabel: "PM", color: "#daa520" },
            { key: "takeawaysTotal", label: "Takeaways", shortLabel: "TA", color: "#98fb98" },
        ],
    },
    effortPoints: {
        title: "Effort Metrics",
        secondaryTitle: null,
        metrics: [
            { key: "topSpeed", label: "Top Speed (km/h)", shortLabel: "TS", color: "#9932cc" },
            { key: "distancePer20", label: "Distance Per 20min (m)", shortLabel: "D20", color: "#4169e1" },
            { key: "accelerationsPer20", label: "Accelerations Per 20min", shortLabel: "ACC", color: "#8a2be2" },
            { key: "decelerationsPer20", label: "Decelerations Per 20min", shortLabel: "DEC", color: "#ba55d3" },
            { key: "averageSpeedWithPuck", label: "Avg Speed With Puck (km/h)", shortLabel: "ASWP", color: "#6a0dad" },
        ],
    },
    bonusPoints: {
        title: "Bonus Points",
        secondaryTitle: null,
        metrics: [
            { key: "goals", label: "Goals", shortLabel: "G", color: "#ff4500" },
            { key: "assists", label: "Assists", shortLabel: "A", color: "#ffa500" },
        ],
    },
    timePoints: {
        title: "Time Data Analysis",
        secondaryTitle: null,
        metrics: [
            { key: "timeOnIceTotal", label: "Total Time on Ice", shortLabel: "Total", color: "#ff69b4" },
            { key: "timeZoneOffensiveMinutes", label: "Offensive Zone (min)", shortLabel: "OFF", color: "#ff1493" },
            { key: "timeZoneDefensiveMinutes", label: "Defensive Zone (min)", shortLabel: "DEF", color: "#dc143c" },
            { key: "timeZoneNeutralMinutes", label: "Neutral Zone (min)", shortLabel: "NEU", color: "#c71585" },
            { key: "timeZoneOffensivePercentage", label: "Offensive Zone (%)", shortLabel: "OFF%", color: "#ff20b2" },
            { key: "timeZoneDefensivePercentage", label: "Defensive Zone (%)", shortLabel: "DEF%", color: "#b8860b" },
            { key: "timeZoneNeutralPercentage", label: "Neutral Zone (%)", shortLabel: "NEU%", color: "#9370db" },
            { key: "averageShiftLength", label: "Average Shift Length", shortLabel: "Shift", color: "#dda0dd" },
        ],
    },
};

const generateRawData = (playerData: PlayerData, selectedKPI: string): DetailedChartDataPoint[] => {
    if (!playerData?.kpis || playerData.kpis.length === 0) return [];

    const kpis = playerData.kpis.sort((a, b) => new Date(a.matchDate).getTime() - new Date(b.matchDate).getTime());

    return kpis.map((kpi, index) => {
        const baseData = {
            date: dayjs(kpi.matchDate).format(dateFormat),
            matchDate: kpi.matchDate,
            matchNumber: index + 1,
        };

        const rawData: Partial<DetailedChartDataPoint> = {};

        switch (selectedKPI) {
            case "offensivePoints": {
                rawData.accurateShots = kpi.offensiveData.shots.accurateShots;
                rawData.missedShots = kpi.offensiveData.shots.missedShots;
                rawData.totalShots = kpi.offensiveData.shots.totalShots;

                rawData.successfulPassesData = kpi.offensiveData.successfulPasses.successfulPassesData;
                rawData.successfulPassesUnderPressure =
                    kpi.offensiveData.successfulPasses.successfulPassesUnderPressure;
                rawData.successfulPassesUnderHighPressure =
                    kpi.offensiveData.successfulPasses.successfulPassesUnderHighPressure;

                rawData.failedPassesData = kpi.offensiveData.failedPasses.failedPassesData;
                rawData.failedPassesUnderPressure = kpi.offensiveData.failedPasses.failedPassesUnderPressure;
                rawData.failedPassesUnderHighPressure = kpi.offensiveData.failedPasses.failedPassesUnderHighPressure;

                const totalShots = kpi.offensiveData.shots.totalShots;
                const totalPasses =
                    kpi.offensiveData.successfulPasses.successfulPassesData +
                    kpi.offensiveData.failedPasses.failedPassesData;
                const totalPassesUnderPressure =
                    kpi.offensiveData.successfulPasses.successfulPassesUnderPressure +
                    kpi.offensiveData.failedPasses.failedPassesUnderPressure;
                const totalPassesUnderHighPressure =
                    kpi.offensiveData.successfulPasses.successfulPassesUnderHighPressure +
                    kpi.offensiveData.failedPasses.failedPassesUnderHighPressure;

                rawData.goalRate =
                    kpi.bonusData.goals > 0 && totalShots > 0 ? (kpi.bonusData.goals / totalShots) * 100 : 0;
                rawData.accurateShotRate =
                    totalShots > 0 ? (kpi.offensiveData.shots.accurateShots / totalShots) * 100 : 0;
                rawData.successPassRate =
                    totalPasses > 0 ? (kpi.offensiveData.successfulPasses.successfulPassesData / totalPasses) * 100 : 0;
                rawData.successPassUnderPressureRate =
                    totalPassesUnderPressure > 0
                        ? (kpi.offensiveData.successfulPasses.successfulPassesUnderPressure /
                              totalPassesUnderPressure) *
                          100
                        : 0;
                rawData.successPassUnderHighPressureRate =
                    totalPassesUnderHighPressure > 0
                        ? (kpi.offensiveData.successfulPasses.successfulPassesUnderHighPressure /
                              totalPassesUnderHighPressure) *
                          100
                        : 0;

                rawData.totalXg = kpi.offensiveData.totalXg;
                rawData.xgFor = kpi.offensiveData.xgFor;
                rawData.pressureReceived = kpi.offensiveData.pressureReceived;
                rawData.averagePressureReceived = kpi.offensiveData.pressureReceived * 100;
                rawData.fullStrengthShootingPercentage = totalShots > 0 ? (kpi.bonusData.goals / totalShots) * 100 : 0;
                rawData.puckProtectionPPMTotal = kpi.offensiveData.puckProtectionPPM.total || 0;
                rawData.puckProtectionPPMOffensive = kpi.offensiveData.puckProtectionPPM.offensive;
                rawData.puckProtectionPPMDefensive = kpi.offensiveData.puckProtectionPPM.defensive;
                rawData.puckProtectionPPMNeutral = kpi.offensiveData.puckProtectionPPM.neutral;
                rawData.puckProtectionEventsTotal = kpi.offensiveData.puckProtectionEvents.total || 0;
                rawData.puckProtectionEventsOffensive = kpi.offensiveData.puckProtectionEvents.offensive;
                rawData.puckProtectionEventsNeutral = kpi.offensiveData.puckProtectionEvents.neutral;
                rawData.puckProtectionEventsDefensive = kpi.offensiveData.puckProtectionEvents.defensive;
                rawData.turnoversTotal = kpi.teamPlayData.turnovers.total || 0;
                rawData.turnoversOffensive = kpi.teamPlayData.turnovers.offensive;
                rawData.turnoversNeutral = kpi.teamPlayData.turnovers.neutral;
                rawData.turnoversDefensive = kpi.teamPlayData.turnovers.defensive;
                break;
            }
            case "defensivePoints":
                rawData.pressureGiven = kpi.defensiveData.pressureGiven;
                rawData.averagePressureGiven = kpi.defensiveData.pressureGiven * 100;
                rawData.highRiskShotsAgainst = kpi.defensiveData.highRiskShotsAgainst;
                rawData.xgAgainst = kpi.defensiveData.xgAgainst;
                rawData.blockedShots = kpi.defensiveData.blockedShots;
                rawData.puckClearances = kpi.defensiveData.puckClearances || 0;
                rawData.puckContestsWinRate = kpi.defensiveData.puckContestsWinRate * 100;
                rawData.contestsTotal = kpi.defensiveData.contests.total || 0;
                rawData.contestsOffensive = kpi.defensiveData.contests.offensive;
                rawData.contestsNeutral = kpi.defensiveData.contests.neutral;
                rawData.contestsDefensive = kpi.defensiveData.contests.defensive;
                rawData.contestsWonTotal = kpi.defensiveData.contestsWon.total || 0;
                rawData.contestsWonDefensive = kpi.defensiveData.contestsWon.defensive;
                rawData.contestsWonNeutral = kpi.defensiveData.contestsWon.neutral;
                rawData.contestsWonOffensive = kpi.defensiveData.contestsWon.offensive;
                break;
            case "teamPlayPoints":
                rawData.plusMinusRaw = kpi.teamPlayData.plusMinusTotal;
                rawData.corsiForPercentage = kpi.teamPlayData.corsiForPercentage * 100;
                rawData.relativeCorsiForPercentage = kpi.teamPlayData.relativeCorsiForPercentage * 100;
                rawData.fenwickForPercentage = kpi.teamPlayData.fenwickForPercentage * 100;
                rawData.relativeFenwickForPercentage = kpi.teamPlayData.relativeFenwickForPercentage * 100;
                rawData.penaltyMinutes = kpi.teamPlayData.penaltyMinutes;
                rawData.takeawaysTotal = kpi.teamPlayData.takeaways.total || 0;
                rawData.takeawaysOffensive = kpi.teamPlayData.takeaways.offensive;
                rawData.takeawaysNeutral = kpi.teamPlayData.takeaways.neutral;
                rawData.takeawaysDefensive = kpi.teamPlayData.takeaways.defensive;
                break;
            case "effortPoints":
                rawData.topSpeed = kpi.effortData.topSpeed;
                rawData.distancePer20 = kpi.effortData.distanceTravelled;
                rawData.distanceTravelled = kpi.effortData.distanceTravelled;
                rawData.accelerationsPer20 = kpi.effortData.accelerationsPer20;
                rawData.decelerationsPer20 = kpi.effortData.decelerationsPer20;
                rawData.averageSpeedWithPuck = kpi.effortData.averageSpeedWithPuck || 0;
                rawData.puckControlForwardDistance = kpi.effortData.puckCarrying.puckControlForwardDistance;
                rawData.puckControlForwardDistancePercentage =
                    kpi.effortData.puckCarrying.puckControlForwardDistancePercentage;
                break;
            case "bonusPoints":
                rawData.goals = kpi.bonusData.goals;
                rawData.assists = kpi.bonusData.assists;
                break;
            case "timePoints":
                rawData.timeOnIceTotal = kpi.timeData.timeOnIce.total / 60;

                rawData.timeZoneOffensiveMinutes = kpi.timeData.timeOnIce.offensive / 60;
                rawData.timeZoneDefensiveMinutes = kpi.timeData.timeOnIce.defensive / 60;
                rawData.timeZoneNeutralMinutes = kpi.timeData.timeOnIce.neutral / 60;

                rawData.timeZoneOffensivePercentage = kpi.timeData.timeOnIcePercentage?.offensive || 0;
                rawData.timeZoneDefensivePercentage = kpi.timeData.timeOnIcePercentage?.defensive || 0;
                rawData.timeZoneNeutralPercentage = kpi.timeData.timeOnIcePercentage?.neutral || 0;

                rawData.averageShiftLength = kpi.timeData.averageShiftLength;
                break;
            default:
                rawData.offensivePoints = kpi.offensiveData.points;
                rawData.defensivePoints = kpi.defensiveData.points;
                rawData.teamPlayPoints = kpi.teamPlayData.points;
                rawData.effortPoints = kpi.effortData.points;
                rawData.bonusPoints = kpi.bonusData.points;
                break;
        }

        return { ...baseData, ...rawData } as DetailedChartDataPoint;
    });
};

interface PlayerKPIDetailsChartProps {
    playerData?: PlayerData;
    selectedKPI: string;
    isLoading?: boolean;
    onTitleChange?: (title: { main: string; sub: string; subColor?: string }) => void;
}

export function PlayerKPIDetailsChart({
    playerData,
    selectedKPI,
    isLoading,
    onTitleChange,
}: PlayerKPIDetailsChartProps) {
    const [visibleMetrics, setVisibleMetrics] = React.useState<string[]>([]);
    const [showTrendLine, setShowTrendLine] = React.useState(true);
    const isMobile = useIsMobile();
    const [timeChartType, setTimeChartType] = React.useState<"zoneMinutes" | "zonePercentages" | "shiftLength">(
        "zoneMinutes"
    );
    const [offensiveChartType, setOffensiveChartType] = React.useState<
        "shots" | "successfulPasses" | "failedPasses" | "xg" | "pressure" | "puckProtectionPPM" | "puckProtectionEvents"
    >("shots");
    const [defensiveChartType, setDefensiveChartType] = React.useState<
        | "pressureGiven"
        | "highRiskShotsAgainst"
        | "xgAgainst"
        | "blockedShots"
        | "puckClearances"
        | "puckContestsWinRate"
        | "contests"
        | "contestsWon"
    >("pressureGiven");
    const [effortChartType, setEffortChartType] = React.useState<
        "speed" | "distance" | "accelerations" | "puckControl"
    >("speed");
    const [showTurnoverBreakdown, setShowTurnoverBreakdown] = React.useState(false);
    const [showPuckProtectionBreakdown, setShowPuckProtectionBreakdown] = React.useState(false);
    const [showTakeawaysBreakdown, setShowTakeawaysBreakdown] = React.useState(false);
    const [showContestsBreakdown, setShowContestsBreakdown] = React.useState(false);
    const [showContestsWonBreakdown, setShowContestsWonBreakdown] = React.useState(false);

    const chartData = React.useMemo(() => {
        if (!playerData) return [];
        const rawData = generateRawData(playerData, selectedKPI);
        if (showTrendLine && visibleMetrics.length > 0) {
            const metricKey = visibleMetrics[0];
            return calculateTrendLine(rawData, metricKey);
        }
        return rawData;
    }, [playerData, selectedKPI, visibleMetrics, showTrendLine]);

    const baseKpiConfig = kpiRawDataMapping[selectedKPI as keyof typeof kpiRawDataMapping];

    const kpiConfig = React.useMemo(() => {
        if (selectedKPI === "offensivePoints" && showTurnoverBreakdown) {
            return {
                title: "Turnovers by Zone",
                secondaryTitle: null,
                metrics: [
                    {
                        key: "turnoversOffensive",
                        label: "Turnovers - Offensive Zone",
                        shortLabel: "OFF",
                        color: "#dc2626",
                    },
                    {
                        key: "turnoversNeutral",
                        label: "Turnovers - Neutral Zone",
                        shortLabel: "NEU",
                        color: "var(--color-medal-gold)",
                    },
                    {
                        key: "turnoversDefensive",
                        label: "Turnovers - Defensive Zone",
                        shortLabel: "DEF",
                        color: "var(--color-chart-green)",
                    },
                ],
            };
        }

        if (selectedKPI === "offensivePoints" && showPuckProtectionBreakdown) {
            return {
                title: "Puck Protection Events by Zone",
                secondaryTitle: null,
                metrics: [
                    {
                        key: "puckProtectionEventsOffensive",
                        label: "Puck Protection - Offensive Zone",
                        shortLabel: "OFF",
                        color: "#dc2626",
                    },
                    {
                        key: "puckProtectionEventsNeutral",
                        label: "Puck Protection - Neutral Zone",
                        shortLabel: "NEU",
                        color: "var(--color-medal-gold)",
                    },
                    {
                        key: "puckProtectionEventsDefensive",
                        label: "Puck Protection - Defensive Zone",
                        shortLabel: "DEF",
                        color: "var(--color-chart-green)",
                    },
                ],
            };
        }

        if (selectedKPI === "defensivePoints" && showContestsBreakdown) {
            return {
                secondaryTitle: null,
                title: "Total Contests by Zone",
                metrics: [
                    {
                        key: "contestsOffensive",
                        label: "Contests - Offensive Zone",
                        shortLabel: "OFF",
                        color: "#dc2626",
                    },
                    {
                        key: "contestsNeutral",
                        label: "Contests - Neutral Zone",
                        shortLabel: "NEU",
                        color: "var(--color-medal-gold)",
                    },
                    {
                        key: "contestsDefensive",
                        label: "Contests - Defensive Zone",
                        shortLabel: "DEF",
                        color: "var(--color-chart-green)",
                    },
                ],
            };
        }

        if (selectedKPI === "defensivePoints" && showContestsWonBreakdown) {
            return {
                secondaryTitle: null,
                title: "Total Contests Won by Zone",
                metrics: [
                    {
                        key: "contestsWonOffensive",
                        label: "Contests Won - Offensive Zone",
                        shortLabel: "OFF",
                        color: "#dc2626",
                    },
                    {
                        key: "contestsWonNeutral",
                        label: "Contests Won - Neutral Zone",
                        shortLabel: "NEU",
                        color: "var(--color-medal-gold)",
                    },
                    {
                        key: "contestsWonDefensive",
                        label: "Contests Won - Defensive Zone",
                        shortLabel: "DEF",
                        color: "var(--color-chart-green)",
                    },
                ],
            };
        }

        if (selectedKPI === "teamPlayPoints" && showTakeawaysBreakdown) {
            return {
                title: "Takeaways by Zone",
                metrics: [
                    {
                        key: "takeawaysOffensive",
                        label: "Takeaways - Offensive Zone",
                        shortLabel: "OFF",
                        color: "#dc2626",
                    },
                    {
                        key: "takeawaysNeutral",
                        label: "Takeaways - Neutral Zone",
                        shortLabel: "NEU",
                        color: "var(--color-medal-gold)",
                    },
                    {
                        key: "takeawaysDefensive",
                        label: "Takeaways - Defensive Zone",
                        shortLabel: "DEF",
                        color: "var(--color-chart-green)",
                    },
                ],
            };
        }

        if (selectedKPI === "offensivePoints") {
            switch (offensiveChartType) {
                case "shots":
                    return {
                        title: "Shots Breakdown",
                        secondaryTitle: null,
                        metrics: [
                            {
                                key: "accurateShots",
                                label: "Accurate Shots",
                                shortLabel: "ACC",
                                color: "#22c55e",
                            },
                            {
                                key: "missedShots",
                                label: "Missed Shots",
                                shortLabel: "MISS",
                                color: "#ef4444",
                            },
                        ],
                    };
                case "successfulPasses":
                    return {
                        title: "Successful Passes",
                        metrics: [
                            {
                                key: "successfulPassesData",
                                label: "All Successful Passes",
                                shortLabel: "ALL",
                                color: "var(--color-chart-blue)",
                            },
                            {
                                key: "successfulPassesUnderPressure",
                                label: "Under Pressure",
                                shortLabel: "PRES",
                                color: "#f59e0b",
                            },
                            {
                                key: "successfulPassesUnderHighPressure",
                                label: "Under High Pressure",
                                shortLabel: "HIGH",
                                color: "#ef4444",
                            },
                        ],
                    };
                case "failedPasses":
                    return {
                        title: "Failed Passes",
                        secondaryTitle: null,
                        metrics: [
                            {
                                key: "failedPassesData",
                                label: "All Failed Passes",
                                shortLabel: "ALL",
                                color: "#64748b",
                            },
                            {
                                key: "failedPassesUnderPressure",
                                label: "Under Pressure",
                                shortLabel: "PRES",
                                color: "#f59e0b",
                            },
                            {
                                key: "failedPassesUnderHighPressure",
                                label: "Under High Pressure",
                                shortLabel: "HIGH",
                                color: "#dc2626",
                            },
                        ],
                    };
                case "xg":
                    return {
                        title: "Expected Goals (xG)",
                        secondaryTitle: null,
                        metrics: [
                            {
                                key: "totalXg",
                                label: "Total xG",
                                shortLabel: "Total",
                                color: "#8b5cf6",
                            },
                            {
                                key: "xgFor",
                                label: "xG For",
                                shortLabel: "xGF",
                                color: "#06b6d4",
                            },
                        ],
                    };
                case "pressure":
                    return {
                        title: "Pressure Received",
                        metrics: [
                            {
                                key: "pressureReceived",
                                label: "Pressure Received",
                                shortLabel: "PRES",
                                color: "#f59e0b",
                            },
                        ],
                    };
                case "puckProtectionPPM":
                    return {
                        title: "Puck Protection PPM by Zone",
                        metrics: [
                            {
                                key: "puckProtectionPPMOffensive",
                                label: "Offensive Zone",
                                shortLabel: "OFF",
                                color: "#dc2626",
                            },
                            {
                                key: "puckProtectionPPMNeutral",
                                label: "Neutral Zone",
                                shortLabel: "NEU",
                                color: "var(--color-medal-gold)",
                            },
                            {
                                key: "puckProtectionPPMDefensive",
                                label: "Defensive Zone",
                                shortLabel: "DEF",
                                color: "var(--color-chart-green)",
                            },
                        ],
                    };
                case "puckProtectionEvents":
                    return {
                        title: "Puck Protection Events by Zone",
                        secondaryTitle: null,
                        metrics: [
                            {
                                key: "puckProtectionEventsOffensive",
                                label: "Offensive Zone",
                                shortLabel: "OFF",
                                color: "#dc2626",
                            },
                            {
                                key: "puckProtectionEventsNeutral",
                                label: "Neutral Zone",
                                shortLabel: "NEU",
                                color: "var(--color-medal-gold)",
                            },
                            {
                                key: "puckProtectionEventsDefensive",
                                label: "Defensive Zone",
                                shortLabel: "DEF",
                                color: "var(--color-chart-green)",
                            },
                        ],
                    };
                default:
                    return baseKpiConfig;
            }
        }

        if (selectedKPI === "effortPoints") {
            switch (effortChartType) {
                case "speed":
                    return {
                        title: "Speed Metrics",
                        metrics: [
                            {
                                key: "topSpeed",
                                label: "Top Speed (km/h)",
                                shortLabel: "TOP",
                                color: "#8b5cf6",
                            },
                            {
                                key: "averageSpeedWithPuck",
                                label: "Avg Speed With Puck (km/h)",
                                shortLabel: "AVG",
                                color: "#06b6d4",
                            },
                        ],
                    };
                case "distance":
                    return {
                        title: "Distance Travelled",
                        secondaryTitle: null,
                        metrics: [
                            {
                                key: "distanceTravelled",
                                label: "Distance Travelled (m)",
                                shortLabel: "DIST",
                                color: "var(--color-chart-blue)",
                            },
                        ],
                    };
                case "accelerations":
                    return {
                        title: "Accelerations & Decelerations",
                        secondaryTitle: null,
                        metrics: [
                            {
                                key: "accelerationsPer20",
                                label: "Accelerations Per 20min",
                                shortLabel: "ACC",
                                color: "#22c55e",
                            },
                            {
                                key: "decelerationsPer20",
                                label: "Decelerations Per 20min",
                                shortLabel: "DEC",
                                color: "#ef4444",
                            },
                        ],
                    };
                case "puckControl":
                    return {
                        title: "Puck Carrying",
                        secondaryTitle: "Puck Control Distance",
                        metrics: [
                            {
                                key: "puckControlForwardDistance",
                                label: "Forward Distance (m)",
                                shortLabel: "DIST",
                                color: "#10b981",
                            },
                            {
                                key: "puckControlForwardDistancePercentage",
                                label: "Forward Distance %",
                                shortLabel: "DIST%",
                                color: "#06b6d4",
                            },
                        ],
                    };
                default:
                    return baseKpiConfig;
            }
        }

        if (selectedKPI === "defensivePoints") {
            switch (defensiveChartType) {
                case "pressureGiven":
                    return {
                        title: "Pressure Given",
                        metrics: [
                            {
                                key: "pressureGiven",
                                label: "Pressure Given",
                                shortLabel: "PRES",
                                color: "#22c55e",
                            },
                        ],
                    };
                case "highRiskShotsAgainst":
                    return {
                        title: "High Risk Shots Against",
                        metrics: [
                            {
                                key: "highRiskShotsAgainst",
                                label: "High Risk Shots Against",
                                shortLabel: "HRSA",
                                color: "#ef4444",
                            },
                        ],
                    };
                case "xgAgainst":
                    return {
                        title: "Expected Goals Against (xGA)",
                        metrics: [
                            {
                                key: "xgAgainst",
                                label: "xG Against",
                                shortLabel: "xGA",
                                color: "#f59e0b",
                            },
                        ],
                    };
                case "blockedShots":
                    return {
                        title: "Blocked Shots",
                        metrics: [
                            {
                                key: "blockedShots",
                                label: "Blocked Shots",
                                shortLabel: "BLK",
                                color: "var(--color-chart-blue)",
                            },
                        ],
                    };
                case "puckClearances":
                    return {
                        title: "Puck Clearances",
                        metrics: [
                            {
                                key: "puckClearances",
                                label: "Puck Clearances",
                                shortLabel: "CLR",
                                color: "#8b5cf6",
                            },
                        ],
                    };
                case "puckContestsWinRate":
                    return {
                        title: "Puck Contest Win Rate (%)",
                        metrics: [
                            {
                                key: "puckContestsWinRate",
                                label: "Contest Win Rate (%)",
                                shortLabel: "WR%",
                                color: "#10b981",
                            },
                        ],
                    };
                case "contests":
                    return {
                        title: "Contests by Zone",
                        metrics: [
                            {
                                key: "contestsOffensive",
                                label: "Offensive Zone",
                                shortLabel: "OFF",
                                color: "#dc2626",
                            },
                            {
                                key: "contestsNeutral",
                                label: "Neutral Zone",
                                shortLabel: "NEU",
                                color: "var(--color-medal-gold)",
                            },
                            {
                                key: "contestsDefensive",
                                label: "Defensive Zone",
                                shortLabel: "DEF",
                                color: "var(--color-chart-green)",
                            },
                        ],
                    };
                case "contestsWon":
                    return {
                        title: "Contests Won by Zone",
                        metrics: [
                            {
                                key: "contestsWonOffensive",
                                label: "Offensive Zone",
                                shortLabel: "OFF",
                                color: "#dc2626",
                            },
                            {
                                key: "contestsWonNeutral",
                                label: "Neutral Zone",
                                shortLabel: "NEU",
                                color: "var(--color-medal-gold)",
                            },
                            {
                                key: "contestsWonDefensive",
                                label: "Defensive Zone",
                                shortLabel: "DEF",
                                color: "var(--color-chart-green)",
                            },
                        ],
                    };
                default:
                    return baseKpiConfig;
            }
        }

        if (selectedKPI === "timePoints") {
            switch (timeChartType) {
                case "zoneMinutes":
                    return {
                        title: "Zone Time Distribution (min)",
                        metrics: [
                            {
                                key: "timeZoneOffensiveMinutes",
                                label: "Offensive Zone (min)",
                                shortLabel: "OFF",
                                color: "#dc2626",
                            },
                            {
                                key: "timeZoneNeutralMinutes",
                                label: "Neutral Zone (min)",
                                shortLabel: "NEU",
                                color: "var(--color-medal-gold)",
                            },
                            {
                                key: "timeZoneDefensiveMinutes",
                                label: "Defensive Zone (min)",
                                shortLabel: "DEF",
                                color: "var(--color-chart-green)",
                            },
                        ],
                    };
                case "zonePercentages":
                    return {
                        title: "Zone Time Distribution (%)",
                        metrics: [
                            {
                                key: "timeZoneOffensivePercentage",
                                label: "Offensive Zone (%)",
                                shortLabel: "OFF%",
                                color: "#dc2626",
                            },
                            {
                                key: "timeZoneNeutralPercentage",
                                label: "Neutral Zone (%)",
                                shortLabel: "NEU%",
                                color: "var(--color-medal-gold)",
                            },
                            {
                                key: "timeZoneDefensivePercentage",
                                label: "Defensive Zone (%)",
                                shortLabel: "DEF%",
                                color: "var(--color-chart-green)",
                            },
                        ],
                    };
                case "shiftLength":
                    return {
                        title: "Average Shift Length",
                        metrics: [
                            {
                                key: "averageShiftLength",
                                label: "Avg Shift Length (s)",
                                shortLabel: "Shift",
                                color: "#dda0dd",
                            },
                        ],
                    };
                default:
                    return baseKpiConfig;
            }
        }
        return baseKpiConfig;
    }, [
        baseKpiConfig,
        selectedKPI,
        offensiveChartType,
        defensiveChartType,
        effortChartType,
        timeChartType,
        showTurnoverBreakdown,
        showPuckProtectionBreakdown,
        showTakeawaysBreakdown,
        showContestsBreakdown,
        showContestsWonBreakdown,
    ]);

    const chartConfig = React.useMemo(() => {
        if (!kpiConfig) return {} as ChartConfig;

        const config: ChartConfig = {};
        kpiConfig.metrics.forEach((metric) => {
            config[metric.key] = {
                label: metric.label,
                color: metric.color,
            };
        });
        return config;
    }, [kpiConfig]);

    const isStackedChart =
        selectedKPI === "totalPoints" ||
        (selectedKPI === "timePoints" && (timeChartType === "zoneMinutes" || timeChartType === "zonePercentages")) ||
        (selectedKPI === "offensivePoints" &&
            (offensiveChartType === "shots" ||
                offensiveChartType === "successfulPasses" ||
                offensiveChartType === "failedPasses" ||
                offensiveChartType === "xg" ||
                offensiveChartType === "puckProtectionPPM" ||
                offensiveChartType === "puckProtectionEvents" ||
                showTurnoverBreakdown ||
                showPuckProtectionBreakdown)) ||
        (selectedKPI === "defensivePoints" &&
            (defensiveChartType === "contests" ||
                defensiveChartType === "contestsWon" ||
                showContestsBreakdown ||
                showContestsWonBreakdown)) ||
        (selectedKPI === "effortPoints" &&
            (effortChartType === "speed" ||
                effortChartType === "accelerations" ||
                effortChartType === "puckControl")) ||
        (selectedKPI === "teamPlayPoints" && showTakeawaysBreakdown);

    const minWidthPerPoint = React.useMemo(() => {
        if (!isMobile || chartData.length === 0 || isStackedChart) return 20;
        let maxLen = 2;
        for (const metricKey of visibleMetrics) {
            for (const d of chartData) {
                const v = d[metricKey];
                if (typeof v !== "number") continue;
                const str = v % 1 === 0 ? v.toString() : v.toFixed(1);
                if (str.length > maxLen) maxLen = str.length;
            }
        }
        return Math.max(20, maxLen * 9);
    }, [chartData, visibleMetrics, isMobile, isStackedChart]);

    React.useEffect(() => {
        if (kpiConfig) {
            if (isStackedChart) {
                setVisibleMetrics(kpiConfig.metrics.map((m) => m.key));
            } else if (
                visibleMetrics.length === 0 ||
                (visibleMetrics.some(
                    (m) =>
                        m.startsWith("turnovers") ||
                        m.startsWith("puckProtection") ||
                        m.startsWith("takeaways") ||
                        m.startsWith("contests")
                ) &&
                    !showTurnoverBreakdown &&
                    !showPuckProtectionBreakdown &&
                    !showTakeawaysBreakdown &&
                    !showContestsBreakdown &&
                    !showContestsWonBreakdown)
            ) {
                setVisibleMetrics([kpiConfig.metrics[0].key]);
            }
        }
    }, [
        kpiConfig,
        isStackedChart,
        showTurnoverBreakdown,
        showPuckProtectionBreakdown,
        showTakeawaysBreakdown,
        showContestsBreakdown,
        showContestsWonBreakdown,
    ]);

    React.useEffect(() => {
        setShowTurnoverBreakdown(false);
        setShowPuckProtectionBreakdown(false);
        setShowTakeawaysBreakdown(false);
        setShowContestsBreakdown(false);
        setShowContestsWonBreakdown(false);
        if (baseKpiConfig) {
            const newIsStackedChart = selectedKPI === "totalPoints";
            if (newIsStackedChart) {
                setVisibleMetrics(baseKpiConfig.metrics.map((m) => m.key));
            } else {
                setVisibleMetrics([baseKpiConfig.metrics[0].key]);
            }
        }
    }, [selectedKPI, baseKpiConfig]);

    React.useEffect(() => {
        if (selectedKPI === "timePoints" && kpiConfig) {
            setVisibleMetrics(kpiConfig.metrics.map((m) => m.key));
        }
    }, [timeChartType, selectedKPI, kpiConfig]);

    React.useEffect(() => {
        if (selectedKPI === "offensivePoints" && kpiConfig) {
            setVisibleMetrics(kpiConfig.metrics.map((m) => m.key));
        }
    }, [offensiveChartType, selectedKPI, kpiConfig]);

    React.useEffect(() => {
        if (selectedKPI === "effortPoints" && kpiConfig) {
            setVisibleMetrics(kpiConfig.metrics.map((m) => m.key));
        }
    }, [effortChartType, selectedKPI, kpiConfig]);

    React.useEffect(() => {
        if (selectedKPI === "defensivePoints" && kpiConfig) {
            setVisibleMetrics(kpiConfig.metrics.map((m) => m.key));
        }
    }, [defensiveChartType, selectedKPI, kpiConfig]);

    const handleMetricToggle = (metricKey: string) => {
        if (metricKey === "turnoversTotal") {
            setShowTurnoverBreakdown(true);
            setShowPuckProtectionBreakdown(false);
            setShowTakeawaysBreakdown(false);
            setShowContestsBreakdown(false);
            setShowContestsWonBreakdown(false);
            setVisibleMetrics(["turnoversOffensive", "turnoversNeutral", "turnoversDefensive"]);
        } else if (metricKey === "puckProtectionEventsTotal") {
            setShowTurnoverBreakdown(false);
            setShowPuckProtectionBreakdown(true);
            setShowTakeawaysBreakdown(false);
            setShowContestsBreakdown(false);
            setShowContestsWonBreakdown(false);
            setVisibleMetrics([
                "puckProtectionEventsOffensive",
                "puckProtectionEventsNeutral",
                "puckProtectionEventsDefensive",
            ]);
        } else if (metricKey === "takeawaysTotal") {
            setShowTurnoverBreakdown(false);
            setShowPuckProtectionBreakdown(false);
            setShowTakeawaysBreakdown(true);
            setShowContestsBreakdown(false);
            setShowContestsWonBreakdown(false);
            setVisibleMetrics(["takeawaysOffensive", "takeawaysNeutral", "takeawaysDefensive"]);
        } else if (metricKey === "contestsTotal") {
            setShowTurnoverBreakdown(false);
            setShowPuckProtectionBreakdown(false);
            setShowTakeawaysBreakdown(false);
            setShowContestsBreakdown(true);
            setShowContestsWonBreakdown(false);
            setVisibleMetrics(["contestsOffensive", "contestsNeutral", "contestsDefensive"]);
        } else if (metricKey === "contestsWonTotal") {
            setShowTurnoverBreakdown(false);
            setShowPuckProtectionBreakdown(false);
            setShowTakeawaysBreakdown(false);
            setShowContestsBreakdown(false);
            setShowContestsWonBreakdown(true);
            setVisibleMetrics(["contestsWonOffensive", "contestsWonNeutral", "contestsWonDefensive"]);
        } else {
            setShowTurnoverBreakdown(false);
            setShowPuckProtectionBreakdown(false);
            setShowTakeawaysBreakdown(false);
            setShowContestsBreakdown(false);
            setShowContestsWonBreakdown(false);
            setVisibleMetrics([metricKey]);
        }
    };

    React.useEffect(() => {
        if (!kpiConfig) return;
        let sub: string;
        if (isStackedChart && selectedKPI === "totalPoints") {
            sub = "Breakout of Total Points";
        } else if (kpiConfig.title !== baseKpiConfig.title) {
            // sub-type selected (e.g. "Pressure Given", "Shots Breakdown")
            sub = kpiConfig.title;
        } else {
            sub =
                kpiConfig.metrics.find((m) => m.key === visibleMetrics[0])?.label || kpiConfig.metrics[0]?.label || "";
        }
        const metricColor =
            kpiConfig.metrics.find((m) => m.key === visibleMetrics[0])?.color || kpiConfig.metrics[0]?.color;
        onTitleChange?.({
            main: baseKpiConfig.title,
            sub,
            subColor: metricColor,
        });
    }, [kpiConfig, baseKpiConfig, isStackedChart, visibleMetrics, onTitleChange, selectedKPI]);

    if (isLoading && !playerData) {
        return (
            <div className="flex items-center justify-center h-[400px]">
                <Loader message="Loading details chart..." className="text-sm" />
            </div>
        );
    }

    if (!playerData || !kpiConfig || chartData.length === 0) {
        return (
            <div className="flex items-center justify-center h-[400px]">
                <p className="text-on-surface-variant">No detailed data available</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full animate-delay-700 p-2 md:p-4">
            <div className="flex flex-wrap gap-2 mb-1 shrink-0 md:flex-col md:items-end">
                {selectedKPI === "timePoints" && (
                    <nav className="rounded-md p-1 bg-surface-container-low basis-full md:basis-auto">
                        <div className="grid grid-cols-3 gap-1 md:flex md:items-center md:space-x-2 md:gap-0">
                            {[
                                {
                                    key: "zoneMinutes",
                                    label: "Zones",
                                    fullLabel: "Zone Time Distribution (min)",
                                    color: "#dc2626",
                                },
                                {
                                    key: "zonePercentages",
                                    label: "Zone %",
                                    fullLabel: "Zone Time Distribution (%)",
                                    color: "#dc2626",
                                },
                                {
                                    key: "shiftLength",
                                    label: "Shifts",
                                    fullLabel: "Average Shift Length",
                                    color: "#dda0dd",
                                },
                            ].map((type) => (
                                <Tooltip key={type.key}>
                                    <TooltipTrigger asChild>
                                        <button
                                            onClick={() => setTimeChartType(type.key as typeof timeChartType)}
                                            className={`nav-tab ${
                                                timeChartType === type.key ? "text-white shadow-sm" : "nav-tab-inactive"
                                            }`}
                                            style={timeChartType === type.key ? { backgroundColor: type.color } : {}}
                                        >
                                            {type.label}
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{type.fullLabel}</p>
                                    </TooltipContent>
                                </Tooltip>
                            ))}
                        </div>
                    </nav>
                )}

                {selectedKPI === "offensivePoints" && (
                    <nav className="rounded-md p-1 bg-surface-container-low basis-full md:basis-auto">
                        <div className="grid grid-cols-3 gap-1 md:flex md:items-center md:space-x-2 md:gap-0">
                            {[
                                { key: "shots", label: "Shots", fullLabel: "Shots Breakdown", color: "#22c55e" },
                                {
                                    key: "successfulPasses",
                                    label: "Success Pass",
                                    fullLabel: "Successful Passes",
                                    color: "var(--color-chart-blue)",
                                },
                                {
                                    key: "failedPasses",
                                    label: "Failed Pass",
                                    fullLabel: "Failed Passes",
                                    color: "#64748b",
                                },
                                { key: "xg", label: "xG", fullLabel: "Expected Goals", color: "#8b5cf6" },
                                {
                                    key: "pressure",
                                    label: "Pressure",
                                    fullLabel: "Pressure Received",
                                    color: "#f59e0b",
                                },
                                {
                                    key: "puckProtectionPPM",
                                    label: "Turnovers",
                                    fullLabel: "Turnovers",
                                    color: "#10b981",
                                },
                                {
                                    key: "puckProtectionEvents",
                                    label: "PP Events",
                                    fullLabel: "Puck Protection Events",
                                    color: "#06b6d4",
                                },
                            ].map((type) => (
                                <Tooltip key={type.key}>
                                    <TooltipTrigger asChild>
                                        <button
                                            onClick={() => setOffensiveChartType(type.key as typeof offensiveChartType)}
                                            className={`nav-tab ${
                                                offensiveChartType === type.key
                                                    ? "text-white shadow-sm"
                                                    : "nav-tab-inactive"
                                            }`}
                                            style={
                                                offensiveChartType === type.key ? { backgroundColor: type.color } : {}
                                            }
                                        >
                                            {type.label}
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{type.fullLabel}</p>
                                    </TooltipContent>
                                </Tooltip>
                            ))}
                        </div>
                    </nav>
                )}

                {selectedKPI === "effortPoints" && (
                    <nav className="rounded-md p-1 bg-surface-container-low basis-full md:basis-auto">
                        <div className="grid grid-cols-3 gap-1 md:flex md:items-center md:space-x-2 md:gap-0">
                            {[
                                { key: "speed", label: "Speed", fullLabel: "Speed Metrics", color: "#8b5cf6" },
                                {
                                    key: "distance",
                                    label: "Distance",
                                    fullLabel: "Distance Travelled",
                                    color: "var(--color-chart-blue)",
                                },
                                {
                                    key: "accelerations",
                                    label: "Acc/Dec",
                                    fullLabel: "Accelerations & Decelerations",
                                    color: "#22c55e",
                                },
                                {
                                    key: "puckControl",
                                    label: "Puck Carrying",
                                    fullLabel: "Puck Carrying",
                                    color: "#10b981",
                                },
                            ].map((type) => (
                                <Tooltip key={type.key}>
                                    <TooltipTrigger asChild>
                                        <button
                                            onClick={() => setEffortChartType(type.key as typeof effortChartType)}
                                            className={`nav-tab ${
                                                effortChartType === type.key
                                                    ? "text-white shadow-sm"
                                                    : "nav-tab-inactive"
                                            }`}
                                            style={effortChartType === type.key ? { backgroundColor: type.color } : {}}
                                        >
                                            {type.label}
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{type.fullLabel}</p>
                                    </TooltipContent>
                                </Tooltip>
                            ))}
                        </div>
                    </nav>
                )}

                {selectedKPI === "defensivePoints" && (
                    <nav className="rounded-md p-1 bg-surface-container-low basis-full md:basis-auto">
                        <div className="grid grid-cols-3 gap-1 md:flex md:items-center md:space-x-2 md:gap-0">
                            {[
                                {
                                    key: "pressureGiven",
                                    label: "Pressure",
                                    fullLabel: "Pressure Given",
                                    color: "#22c55e",
                                },
                                {
                                    key: "highRiskShotsAgainst",
                                    label: "High Risk",
                                    fullLabel: "High Risk Shots Against",
                                    color: "#ef4444",
                                },
                                {
                                    key: "xgAgainst",
                                    label: "xGA",
                                    fullLabel: "Expected Goals Against",
                                    color: "#f59e0b",
                                },
                                {
                                    key: "blockedShots",
                                    label: "Blocks",
                                    fullLabel: "Blocked Shots",
                                    color: "var(--color-chart-blue)",
                                },
                                {
                                    key: "puckClearances",
                                    label: "Clearances",
                                    fullLabel: "Puck Clearances",
                                    color: "#8b5cf6",
                                },
                                {
                                    key: "puckContestsWinRate",
                                    label: "Win Rate",
                                    fullLabel: "Puck Contest Win Rate",
                                    color: "#10b981",
                                },
                                { key: "contests", label: "Contests", fullLabel: "Contests by Zone", color: "#06b6d4" },
                                {
                                    key: "contestsWon",
                                    label: "Won",
                                    fullLabel: "Contests Won by Zone",
                                    color: "#14b8a6",
                                },
                            ].map((type) => (
                                <Tooltip key={type.key}>
                                    <TooltipTrigger asChild>
                                        <button
                                            onClick={() => setDefensiveChartType(type.key as typeof defensiveChartType)}
                                            className={`nav-tab ${
                                                defensiveChartType === type.key
                                                    ? "text-white shadow-sm"
                                                    : "nav-tab-inactive"
                                            }`}
                                            style={
                                                defensiveChartType === type.key ? { backgroundColor: type.color } : {}
                                            }
                                        >
                                            {type.label}
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{type.fullLabel}</p>
                                    </TooltipContent>
                                </Tooltip>
                            ))}
                        </div>
                    </nav>
                )}

                {((!isStackedChart && kpiConfig.metrics.length > 1) ||
                    (selectedKPI === "offensivePoints" && (showTurnoverBreakdown || showPuckProtectionBreakdown)) ||
                    (selectedKPI === "teamPlayPoints" && showTakeawaysBreakdown) ||
                    (selectedKPI === "defensivePoints" && (showContestsBreakdown || showContestsWonBreakdown))) && (
                    <nav className="rounded-md p-1 bg-surface-container-low basis-full md:basis-auto">
                        <div className="grid grid-cols-3 gap-1 md:flex md:items-center md:space-x-2 md:gap-0">
                            {(showTurnoverBreakdown ||
                            showPuckProtectionBreakdown ||
                            showTakeawaysBreakdown ||
                            showContestsBreakdown ||
                            showContestsWonBreakdown
                                ? baseKpiConfig.metrics
                                : kpiConfig.metrics
                            ).map((metric) => (
                                <Tooltip key={metric.key}>
                                    <TooltipTrigger asChild>
                                        <button
                                            onClick={() => handleMetricToggle(metric.key)}
                                            className={`nav-tab ${
                                                (showTurnoverBreakdown && metric.key === "turnoversTotal") ||
                                                (showPuckProtectionBreakdown &&
                                                    metric.key === "puckProtectionEventsTotal") ||
                                                (showTakeawaysBreakdown && metric.key === "takeawaysTotal") ||
                                                (showContestsBreakdown && metric.key === "contestsTotal") ||
                                                (showContestsWonBreakdown && metric.key === "contestsWonTotal") ||
                                                (!showTurnoverBreakdown &&
                                                    !showPuckProtectionBreakdown &&
                                                    !showTakeawaysBreakdown &&
                                                    !showContestsWonBreakdown &&
                                                    !showContestsBreakdown &&
                                                    visibleMetrics.includes(metric.key))
                                                    ? "text-white shadow-sm"
                                                    : "nav-tab-inactive"
                                            }`}
                                            style={
                                                (showTurnoverBreakdown && metric.key === "turnoversTotal") ||
                                                (showPuckProtectionBreakdown &&
                                                    metric.key === "puckProtectionEventsTotal") ||
                                                (showTakeawaysBreakdown && metric.key === "takeawaysTotal") ||
                                                (showContestsBreakdown && metric.key === "contestsTotal") ||
                                                (showContestsWonBreakdown && metric.key === "contestsWonTotal") ||
                                                (!showTurnoverBreakdown &&
                                                    !showPuckProtectionBreakdown &&
                                                    !showTakeawaysBreakdown &&
                                                    !showContestsBreakdown &&
                                                    !showContestsWonBreakdown &&
                                                    visibleMetrics.includes(metric.key))
                                                    ? {
                                                          backgroundColor: metric.color,
                                                      }
                                                    : {}
                                            }
                                        >
                                            {metric.shortLabel || metric.label}
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{metric.label}</p>
                                    </TooltipContent>
                                </Tooltip>
                            ))}
                        </div>
                    </nav>
                )}
                {!isStackedChart && (
                    <div className="basis-full flex justify-center md:basis-auto mt-2 md:mt-0">
                        <label className="flex items-center gap-2 text-xs text-on-surface-variant cursor-pointer select-none">
                            <span>Show trend line</span>
                            <Checkbox
                                checked={showTrendLine}
                                onCheckedChange={(checked) => setShowTrendLine(checked === true)}
                                className="cursor-pointer data-[state=unchecked]:border-on-surface-variant"
                            />
                        </label>
                    </div>
                )}
            </div>

            <div className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden">
                <ChartContainer
                    config={chartConfig}
                    className="h-full w-full"
                    style={{ minWidth: isMobile ? `${Math.max(300, chartData.length * minWidthPerPoint)}px` : "100%" }}
                >
                    <AreaChart
                        data={chartData}
                        margin={{
                            top: 30,
                            right: isMobile ? 8 : 30,
                            left: isMobile ? 8 : 30,
                            bottom: isMobile ? 16 : 5,
                        }}
                        stackOffset={isStackedChart ? "none" : undefined}
                    >
                        <defs>
                            {kpiConfig.metrics
                                .filter((metric) => visibleMetrics.includes(metric.key))
                                .map((metric) => (
                                    <linearGradient
                                        key={metric.key}
                                        id={`fill${metric.key}`}
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop offset="5%" stopColor={metric.color} stopOpacity={0.8} />
                                        <stop offset="95%" stopColor={metric.color} stopOpacity={0.1} />
                                    </linearGradient>
                                ))}
                        </defs>
                        <CartesianGrid vertical={false} stroke="rgba(5, 0, 102, 0.06)" strokeDasharray="3 3" />
                        <XAxis
                            dataKey="date"
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                            tick={
                                isMobile
                                    ? (props: { x?: number; y?: number; index?: number }) => (
                                          <text
                                              x={props.x}
                                              y={(props.y ?? 0) + 12}
                                              fill="#464554"
                                              fontSize={8}
                                              textAnchor="middle"
                                          >
                                              {(props.index ?? 0) + 1}
                                          </text>
                                      )
                                    : chartData.length > 20
                                      ? false
                                      : { fill: "var(--color-gray-400)", fontSize: 10 }
                            }
                            type="category"
                            interval={0}
                            padding={{ left: 20, right: 20 }}
                        />
                        <ChartTooltip
                            cursor={{
                                stroke:
                                    visibleMetrics.length > 0
                                        ? kpiConfig.metrics.find((m) => m.key === visibleMetrics[0])?.color
                                        : "var(--color-chart-blue)",
                                strokeWidth: 2,
                                strokeDasharray: "5 5",
                                strokeOpacity: 0.6,
                            }}
                            content={({ active, payload, label }) => {
                                if (!active || !payload?.length) return null;

                                const dataPoint = chartData.find((item) => item.date === label);
                                if (!dataPoint) return null;

                                const visiblePayload = payload.filter((entry) =>
                                    visibleMetrics.includes(entry.dataKey as string)
                                );

                                return (
                                    <div className="chart-tooltip rounded-md">
                                        <div className="flex flex-col gap-2">
                                            <div className="font-semibold text-sm text-general-blue">
                                                Match {dataPoint.matchNumber} - {label}
                                            </div>
                                            <div className="space-y-1">
                                                {visiblePayload.map((entry) => {
                                                    const metric = kpiConfig.metrics.find(
                                                        (m) => m.key === entry.dataKey
                                                    );
                                                    if (!metric) return null;

                                                    return (
                                                        <div
                                                            key={entry.dataKey}
                                                            className="flex items-center gap-2 text-xs"
                                                        >
                                                            <div
                                                                className="w-3 h-3 rounded-full"
                                                                style={{
                                                                    backgroundColor: metric.color,
                                                                }}
                                                            />
                                                            <span className="text-on-surface">{metric.label}:</span>
                                                            <span
                                                                className="font-bold"
                                                                style={{
                                                                    color: metric.color,
                                                                }}
                                                            >
                                                                {typeof entry.value === "number"
                                                                    ? entry.value % 1 === 0
                                                                        ? entry.value.toString()
                                                                        : entry.value.toFixed(1)
                                                                    : entry.value}
                                                                {metric.key.includes("Rate") ||
                                                                metric.key.includes("Percentage")
                                                                    ? "%"
                                                                    : metric.key.includes("Time") &&
                                                                        !metric.key.includes("Length")
                                                                      ? " min"
                                                                      : metric.key === "averageShiftLength"
                                                                        ? " s"
                                                                        : metric.key === "distancePer20"
                                                                          ? ""
                                                                          : ""}
                                                            </span>
                                                        </div>
                                                    );
                                                })}

                                                {selectedKPI === "timePoints" && timeChartType === "zoneMinutes" && (
                                                    <div className="flex items-center gap-2 text-xs border-t border-outline-variant/20 pt-1 mt-1">
                                                        <div className="w-3 h-3 rounded-full bg-on-surface-variant" />
                                                        <span className="text-on-surface">Total Time:</span>
                                                        <span className="font-bold text-on-surface-variant">
                                                            {(
                                                                ((dataPoint as Record<string, unknown>)
                                                                    .timeOnIceTotal as number) || 0
                                                            ).toFixed(1)}{" "}
                                                            min
                                                        </span>
                                                    </div>
                                                )}

                                                {selectedKPI === "offensivePoints" &&
                                                    offensiveChartType === "shots" && (
                                                        <div className="flex items-center gap-2 text-xs border-t border-outline-variant/20 pt-1 mt-1">
                                                            <div className="w-3 h-3 rounded-full bg-on-surface-variant" />
                                                            <span className="text-on-surface">Total Shots:</span>
                                                            <span className="font-bold text-on-surface-variant">
                                                                {((dataPoint as Record<string, unknown>)
                                                                    .totalShots as number) || 0}
                                                            </span>
                                                        </div>
                                                    )}

                                                {selectedKPI === "offensivePoints" && showTurnoverBreakdown && (
                                                    <div className="flex items-center gap-2 text-xs border-t border-outline-variant/20 pt-1 mt-1">
                                                        <div className="w-3 h-3 rounded-full bg-on-surface-variant" />
                                                        <span className="text-on-surface">Total Turnovers:</span>
                                                        <span className="font-bold text-on-surface-variant">
                                                            {((dataPoint as Record<string, unknown>)
                                                                .turnoversOffensive as number) +
                                                                ((dataPoint as Record<string, unknown>)
                                                                    .turnoversNeutral as number) +
                                                                ((dataPoint as Record<string, unknown>)
                                                                    .turnoversDefensive as number)}
                                                        </span>
                                                    </div>
                                                )}

                                                {selectedKPI === "offensivePoints" && showPuckProtectionBreakdown && (
                                                    <div className="flex items-center gap-2 text-xs border-t border-outline-variant/20 pt-1 mt-1">
                                                        <div className="w-3 h-3 rounded-full bg-on-surface-variant" />
                                                        <span className="text-on-surface">
                                                            Total Puck Protection Events:
                                                        </span>
                                                        <span className="font-bold text-on-surface-variant">
                                                            {((dataPoint as Record<string, unknown>)
                                                                .puckProtectionEventsOffensive as number) +
                                                                ((dataPoint as Record<string, unknown>)
                                                                    .puckProtectionEventsNeutral as number) +
                                                                ((dataPoint as Record<string, unknown>)
                                                                    .puckProtectionEventsDefensive as number)}
                                                        </span>
                                                    </div>
                                                )}

                                                {selectedKPI === "defensivePoints" && showContestsBreakdown && (
                                                    <div className="flex items-center gap-2 text-xs border-t border-outline-variant/20 pt-1 mt-1">
                                                        <div className="w-3 h-3 rounded-full bg-on-surface-variant" />
                                                        <span className="text-on-surface">Total Contests:</span>
                                                        <span className="font-bold text-on-surface-variant">
                                                            {((dataPoint as Record<string, unknown>)
                                                                .contestsOffensive as number) +
                                                                ((dataPoint as Record<string, unknown>)
                                                                    .contestsNeutral as number) +
                                                                ((dataPoint as Record<string, unknown>)
                                                                    .contestsDefensive as number)}
                                                        </span>
                                                    </div>
                                                )}

                                                {selectedKPI === "defensivePoints" && showContestsWonBreakdown && (
                                                    <div className="flex items-center gap-2 text-xs border-t border-outline-variant/20 pt-1 mt-1">
                                                        <div className="w-3 h-3 rounded-full bg-on-surface-variant" />
                                                        <span className="text-on-surface">Total Contests Won:</span>
                                                        <span className="font-bold text-on-surface-variant">
                                                            {((dataPoint as Record<string, unknown>)
                                                                .contestsWonOffensive as number) +
                                                                ((dataPoint as Record<string, unknown>)
                                                                    .contestsWonNeutral as number) +
                                                                ((dataPoint as Record<string, unknown>)
                                                                    .contestsWonDefensive as number)}
                                                        </span>
                                                    </div>
                                                )}

                                                {selectedKPI === "teamPlayPoints" && showTakeawaysBreakdown && (
                                                    <div className="flex items-center gap-2 text-xs border-t border-outline-variant/20 pt-1 mt-1">
                                                        <div className="w-3 h-3 rounded-full bg-on-surface-variant" />
                                                        <span className="text-on-surface">Total Takeaways:</span>
                                                        <span className="font-bold text-on-surface-variant">
                                                            {((dataPoint as Record<string, unknown>)
                                                                .takeawaysOffensive as number) +
                                                                ((dataPoint as Record<string, unknown>)
                                                                    .takeawaysNeutral as number) +
                                                                ((dataPoint as Record<string, unknown>)
                                                                    .takeawaysDefensive as number)}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            }}
                        />
                        {kpiConfig.metrics
                            .filter((metric) => visibleMetrics.includes(metric.key))
                            .map((metric) => {
                                const yAxisId = undefined;

                                return (
                                    <Area
                                        key={metric.key}
                                        dataKey={metric.key}
                                        type="linear"
                                        fill={`url(#fill${metric.key})`}
                                        stroke={metric.color}
                                        strokeWidth={isStackedChart ? 2 : 3}
                                        stackId={isStackedChart ? "stack" : undefined}
                                        yAxisId={yAxisId}
                                        dot={
                                            isStackedChart
                                                ? false
                                                : {
                                                      r: 4,
                                                      fill: metric.color,
                                                      strokeWidth: 0,
                                                  }
                                        }
                                        activeDot={
                                            isStackedChart
                                                ? false
                                                : {
                                                      r: 6,
                                                      fill: metric.color,
                                                      strokeWidth: 2,
                                                      stroke: "white",
                                                  }
                                        }
                                    >
                                        {!isStackedChart && visibleMetrics.includes(metric.key) && (
                                            <LabelList
                                                dataKey={metric.key}
                                                position="top"
                                                content={({ value, index, x, y }) => (
                                                    <TrendLabel
                                                        value={value}
                                                        index={index}
                                                        x={x}
                                                        y={y}
                                                        dataKey={metric.key}
                                                        data={chartData}
                                                        invertTrend={metric.key === "penaltyMinutes"}
                                                    />
                                                )}
                                            />
                                        )}
                                    </Area>
                                );
                            })}
                        {!isStackedChart &&
                            showTrendLine &&
                            visibleMetrics.map((metricKey) => {
                                const yAxisId = undefined;

                                return (
                                    <Line
                                        key={`${metricKey}_trend`}
                                        dataKey={`${metricKey}_trend`}
                                        type="linear"
                                        stroke="var(--color-chart-ref-line)"
                                        strokeWidth={3}
                                        strokeDasharray="5 5"
                                        dot={false}
                                        activeDot={false}
                                        yAxisId={yAxisId}
                                    />
                                );
                            })}
                    </AreaChart>
                </ChartContainer>
            </div>
        </div>
    );
}
